import { z } from 'zod/v3';

type FrontMatterValue =
  | string
  | number
  | boolean
  | null
  | FrontMatterValue[]
  | { [key: string]: FrontMatterValue };

const frontMatterValueSchema: z.ZodType<FrontMatterValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(frontMatterValueSchema),
    z.record(z.string(), frontMatterValueSchema),
  ]),
);

const toStringSchema = z.any().transform(v => String(v));

const knownMetaKeys = new Set([
  'owner',
  'author',
  'links',
  'type',
  'tags',
  'keywords',
  'description',
  'recommended',
]);

export const metaSchema = z.preprocess(
  val => {
    if (!val || typeof val !== 'object' || Array.isArray(val)) {
      return val;
    }
    const obj = val as Record<string, unknown>;
    const custom: Record<string, unknown> = {};
    const processed: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(obj)) {
      if (knownMetaKeys.has(k)) {
        processed[k] = v;
      } else {
        custom[k] = v;
      }
    }
    if (Object.keys(custom).length > 0) {
      processed.custom = custom;
    }
    return processed;
  },
  z.object({
    owner: toStringSchema.optional(),
    author: toStringSchema.optional(),
    links: z
      .array(
        z.object({
          url: toStringSchema,
          title: toStringSchema.optional(),
        }),
      )
      .optional(),
    type: toStringSchema.optional(),
    tags: z.array(toStringSchema).optional(),
    keywords: z.array(toStringSchema).optional(),
    description: toStringSchema.optional(),
    recommended: z
      .any()
      .transform(v => Boolean(v))
      .optional(),
    custom: z.record(frontMatterValueSchema).optional(),
  }),
);

export const pageSchema = z.object({
  url: toStringSchema,
  meta: metaSchema,

  // NOTE (andy.vaughn, 20250202): the optional flag can be
  // removed after all published documentation metadata
  // have titles set
  title: toStringSchema.optional(),

  // NOTE (andy.vaughn, 20250202): the optional flag can be
  // removed after all published documentation metadata
  // have parents
  parents: z
    .array(
      z.object({
        title: toStringSchema,
        url: toStringSchema,
      }),
    )
    .optional(),

  // Owners of the page's source file, resolved from CODEOWNERS with the
  // leading `@` stripped. Only emitted when the page has at least one owner.
  codeowners: z.array(toStringSchema).optional(),
});

export const techDocsExtendedMetadataSchema = z.object({
  pages: z.array(pageSchema).optional(),
  documents: z.array(z.string()).optional(),
});

export type TechDocsExtendedMetadata = z.infer<
  typeof techDocsExtendedMetadataSchema
>;

const entityLinkSchema = z.object({
  url: z.string(),
  title: z.string().optional(),
  icon: z.string().optional(),
  type: z.string().optional(),
});

const pagePathRelatedPageSchema = z.object({
  title: z.string(),
  type: z.string().optional(),
  description: z.string().optional(),
  path: z.string(),
  weight: z.number(),
});

export type PagePathRelatedPage = z.infer<typeof pagePathRelatedPageSchema>;

const compoundEntityRefSchema = z.object({
  kind: z.string(),
  namespace: z.string(),
  name: z.string(),
});

export const techDocsPageInfoSchema = z.object({
  links: z.array(entityLinkSchema).optional(),
  tags: z.array(z.string()),
  keywords: z.array(z.string()).optional(),
  description: z.string().optional(),
  custom: z.record(frontMatterValueSchema).optional(),
  title: z.string().optional(),
  parentTitles: z.array(z.string()),
  relatedPages: z.array(pagePathRelatedPageSchema),
  ownerRef: compoundEntityRefSchema.optional(),
});

export type TechDocsPageInfo = z.infer<typeof techDocsPageInfoSchema>;
